import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { useBrotiaChat } from '../providers/BrotiaChatProvider';
import {
  FileTooLargeError,
  getLocalUploadUri,
  RateLimitedError,
  UnsupportedFileTypeError,
  type BrotiaAttachment,
  type BrotiaMessage,
} from '../api/brotia/chat';

const BROTIA_PURPLE = '#6347c5';
const MAX_VISIBLE_GRID_CELLS = 6;

type ViewerState = { uris: string[]; index: number } | null;

const MessageAlbum: React.FC<{
  attachments: BrotiaAttachment[];
  onOpen: (uris: string[], index: number) => void;
}> = ({ attachments, onOpen }) => {
  const resolved = attachments.map((att) => ({
    attachment: att,
    uri: getLocalUploadUri(att.mediaStoragePath),
  }));
  const resolvedUris = resolved
    .map((r) => r.uri)
    .filter((uri): uri is string => uri !== null);

  const total = attachments.length;

  if (total === 1) {
    const uri = resolved[0].uri;
    if (!uri) return null;
    return (
      <img
        src={uri}
        alt=""
        onClick={() => onOpen(resolvedUris, 0)}
        className="max-h-56 w-full cursor-pointer object-cover"
      />
    );
  }

  const visibleCount = Math.min(total, MAX_VISIBLE_GRID_CELLS);
  const remaining = total - visibleCount;
  const cols = visibleCount === 2 || visibleCount === 4 ? 2 : 3;

  return (
    <div
      className="grid gap-0.5"
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
    >
      {resolved.slice(0, visibleCount).map((cell, idx) => {
        const isLastVisible = idx === visibleCount - 1;
        const overlay = isLastVisible && remaining > 0 ? `+${remaining}` : null;
        const positionInResolved = cell.uri
          ? resolvedUris.indexOf(cell.uri)
          : -1;
        return (
          <div
            key={cell.attachment.index}
            className="relative aspect-square cursor-pointer overflow-hidden bg-zinc-200"
            onClick={() =>
              cell.uri &&
              positionInResolved >= 0 &&
              onOpen(resolvedUris, positionInResolved)
            }
          >
            {cell.uri && (
              <img
                src={cell.uri}
                alt=""
                className="h-full w-full object-cover"
              />
            )}
            {overlay && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-lg font-bold text-white">
                {overlay}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ImageViewer: React.FC<{
  state: ViewerState;
  onClose: () => void;
}> = ({ state, onClose }) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (state) setIndex(state.index);
  }, [state]);

  if (!state) return null;
  const total = state.uris.length;

  return (
    <Dialog open onClose={onClose} className="relative z-[70]">
      <div className="fixed inset-0 bg-black" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center">
        <button
          type="button"
          aria-label={t('close')}
          onClick={onClose}
          className="absolute right-4 top-4 z-10 text-3xl leading-none text-white"
        >
          &times;
        </button>
        {total > 1 && (
          <>
            <button
              type="button"
              onClick={() => setIndex((i) => (i - 1 + total) % total)}
              className="absolute left-4 z-10 px-3 text-4xl text-white/80 hover:text-white"
            >
              &#8249;
            </button>
            <button
              type="button"
              onClick={() => setIndex((i) => (i + 1) % total)}
              className="absolute right-4 z-10 px-3 text-4xl text-white/80 hover:text-white"
            >
              &#8250;
            </button>
          </>
        )}
        <img
          src={state.uris[index]}
          alt=""
          onClick={onClose}
          className="max-h-[85vh] max-w-[90vw] object-contain"
        />
      </div>
    </Dialog>
  );
};

const MessageBubble: React.FC<{
  message: BrotiaMessage;
  onOpenViewer: (uris: string[], index: number) => void;
}> = ({ message, onOpenViewer }) => {
  const { t } = useTranslation();
  const isMine = message.direction === 'inbound';

  const imageAttachments = message.attachments.filter((att) => {
    const looksImage =
      message.messageType === 'image' ||
      (att.mediaMimeType?.startsWith('image/') ?? false);
    return looksImage && Boolean(att.mediaStoragePath);
  });
  const hasResolvableImages = imageAttachments.some(
    (att) => getLocalUploadUri(att.mediaStoragePath) !== null,
  );
  const showFilenameFallback =
    !hasResolvableImages &&
    ((message.messageType !== undefined && message.messageType !== 'text') ||
      message.attachments.length > 0);

  return (
    <div className={`my-1 flex ${isMine ? 'justify-end' : 'justify-start'}`}>
      <div
        className="max-w-[80%] overflow-hidden rounded-2xl"
        style={{
          backgroundColor: isMine ? BROTIA_PURPLE : '#e4e6eb',
          color: isMine ? '#ffffff' : '#111317',
        }}
      >
        {hasResolvableImages && (
          <MessageAlbum attachments={imageAttachments} onOpen={onOpenViewer} />
        )}
        {showFilenameFallback && (
          <div className="px-3 py-2 text-sm">
            {`📎 ${
              message.attachments[0]?.mediaFilename ??
              message.mediaFilename ??
              t('supportChatAttachment')
            }`}
          </div>
        )}
        {message.text && (
          <div className="whitespace-pre-wrap break-words px-3 py-2 text-sm">
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
};

const SupportChat: React.FC = () => {
  const { t } = useTranslation();
  const {
    isReady,
    error,
    messages,
    sendMessage,
    sendImages,
    markAsRead,
    retry,
    refreshSession,
  } = useBrotiaChat();

  const [text, setText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    uploaded: number;
    total: number;
  } | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [viewer, setViewer] = useState<ViewerState>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const listEndRef = useRef<HTMLDivElement>(null);
  const didRefreshMetadataRef = useRef(false);

  const isUploading = uploadProgress !== null;

  const openViewer = useCallback((uris: string[], index: number) => {
    setViewer({ uris, index });
  }, []);

  // Mark as read whenever the thread changes while the panel is open, and keep
  // the newest message in view.
  useEffect(() => {
    markAsRead();
    listEndRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, markAsRead]);

  // Push current-context metadata once per open so the agent's Context panel is
  // fresh (the session itself is kept alive app-wide for the unread badge).
  useEffect(() => {
    if (didRefreshMetadataRef.current || !isReady) return;
    didRefreshMetadataRef.current = true;
    void refreshSession();
  }, [isReady, refreshSession]);

  const surfaceError = (e: unknown) => {
    if (e instanceof RateLimitedError) {
      setSendError(t('supportChatRateLimited'));
    } else if (e instanceof FileTooLargeError) {
      setSendError(t('supportChatImageTooLarge'));
    } else if (e instanceof UnsupportedFileTypeError) {
      setSendError(t('supportChatImageUnsupportedType'));
    } else {
      setSendError(e instanceof Error ? e.message : String(e));
    }
  };

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || isSending || isUploading) return;
    setIsSending(true);
    setSendError(null);
    try {
      await sendMessage(trimmed);
      setText('');
    } catch (e) {
      surfaceError(e);
    } finally {
      setIsSending(false);
    }
  };

  const handleFilesSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    // Allow re-selecting the same file later.
    event.target.value = '';
    if (files.length === 0 || isSending || isUploading) return;
    setSendError(null);
    setUploadProgress({ uploaded: 0, total: files.length });
    try {
      await sendImages(files, {
        onProgress: (uploaded, total) => {
          setUploadProgress({ uploaded, total });
        },
      });
    } catch (e) {
      surfaceError(e);
    } finally {
      setUploadProgress(null);
    }
  };

  const banner = sendError ?? error;

  return (
    <div className="flex h-full flex-col">
      {!isReady && !error ? (
        <div className="flex flex-1 items-center justify-center">
          <div
            className="h-8 w-8 animate-spin rounded-full border-2 border-t-transparent"
            style={{
              borderColor: BROTIA_PURPLE,
              borderTopColor: 'transparent',
            }}
          />
        </div>
      ) : !isReady && error ? (
        <div className="flex flex-1 flex-col items-center justify-center px-6">
          <p className="mb-4 text-center text-sm text-red-700">{error}</p>
          <button
            type="button"
            onClick={retry}
            className="rounded-lg px-6 py-2 text-sm font-bold text-white"
            style={{ backgroundColor: BROTIA_PURPLE }}
          >
            {t('supportChatRetry')}
          </button>
        </div>
      ) : (
        <>
          {banner && (
            <div className="bg-red-100 px-4 py-2 text-xs text-red-700">
              {banner}
            </div>
          )}
          {isUploading && (
            <div
              className="flex items-center gap-2 px-4 py-2 text-xs"
              style={{ backgroundColor: '#eae4ff', color: BROTIA_PURPLE }}
            >
              <span
                className="h-3 w-3 animate-spin rounded-full border-2 border-t-transparent"
                style={{
                  borderColor: BROTIA_PURPLE,
                  borderTopColor: 'transparent',
                }}
              />
              {uploadProgress && uploadProgress.total > 0
                ? t('supportChatUploadingProgress', {
                    uploaded: uploadProgress.uploaded,
                    total: uploadProgress.total,
                  })
                : t('supportChatUploadingPreparing')}
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-3 py-3">
            {messages.length === 0 ? (
              <div className="flex h-full items-center justify-center px-6">
                <p className="text-center text-sm text-gray-500">
                  {t('supportChatEmpty')}
                </p>
              </div>
            ) : (
              <>
                {messages.map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    onOpenViewer={openViewer}
                  />
                ))}
                <div ref={listEndRef} />
              </>
            )}
          </div>

          <div className="flex items-end gap-2 border-t border-gray-200 px-3 py-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleFilesSelected}
            />
            <button
              type="button"
              aria-label={t('supportChatAttachImage')}
              disabled={isSending || isUploading || !isReady}
              onClick={() => fileInputRef.current?.click()}
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-xl disabled:opacity-40"
              style={{ color: BROTIA_PURPLE }}
              title={t('supportChatAttachImage')}
            >
              📎
            </button>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
              rows={1}
              placeholder={t('supportChatInputPlaceholder')}
              className="max-h-24 flex-1 resize-none rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-900 focus:outline-none"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={isSending || isUploading || !text.trim()}
              aria-label={t('supportChatSend')}
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-white"
              style={{
                backgroundColor:
                  isSending || isUploading || !text.trim()
                    ? '#9c8ddf'
                    : BROTIA_PURPLE,
              }}
            >
              {isSending ? '…' : '➤'}
            </button>
          </div>
        </>
      )}
      <ImageViewer state={viewer} onClose={() => setViewer(null)} />
    </div>
  );
};

export default SupportChat;
